import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  IconButton,
  Button,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import axios from '../api/axios';

// База URL бэкенда (для /uploads)
const getBackendOrigin = () => {
  const api = process.env.REACT_APP_API_URL || '/api';
  if (api.startsWith('http://') || api.startsWith('https://')) {
    return api.replace(/\/api\/?$/, '');
  }
  // Относительный путь или не установлен - используем текущий origin
  return typeof window !== 'undefined' ? window.location.origin : '';
};

// Полный URL для загруженного файла
const getFullVideoSrc = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/uploads')) return getBackendOrigin() + url;
  if (url.startsWith('uploads')) return getBackendOrigin() + '/' + url;
  return url;
};

// Полный URL для изображения
const getFullImageSrc = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  
  // Если путь начинается с /uploads, используем его как есть (относительный путь)
  // Браузер автоматически добавит текущий origin
  if (url.startsWith('/uploads')) {
    return url;
  }
  
  // Если путь начинается с uploads без слеша, добавляем слеш
  if (url.startsWith('uploads')) {
    return '/' + url;
  }
  
  // Если путь начинается с /, используем как есть
  if (url.startsWith('/')) {
    return url;
  }
  
  // Иначе возвращаем как есть
  return url;
};

// MIME-тип по расширению
const getVideoMimeType = (url) => {
  if (!url) return 'video/mp4';
  const ext = (url.split('.').pop() || '').toLowerCase();
  const types = {
    mp4: 'video/mp4',
    m4v: 'video/mp4',
    webm: 'video/webm',
    ogg: 'video/ogg',
    mov: 'video/quicktime',
  };
  return types[ext] || 'video/mp4';
};

const EventsSection = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [galleryIndexes, setGalleryIndexes] = useState({});

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get('/events');
      // Фильтруем только опубликованные события и валидируем данные
      const publishedEvents = (response.data || [])
        .filter(event => event && event.published)
        .map(event => ({
          ...event,
          description: event.description || '',
          gallery: event.gallery || [],
        }));
      setEvents(publishedEvents);
      
      // Инициализируем индексы галерей
      const indexes = {};
      publishedEvents.forEach(event => {
        if (event.mediaType === 'gallery' && event.gallery?.length > 0) {
          indexes[event._id] = 0;
        }
      });
      setGalleryIndexes(indexes);
    } catch (error) {
      console.error('Ошибка загрузки мероприятий:', error);
      setEvents([]); // Устанавливаем пустой массив при ошибке
    } finally {
      setLoading(false);
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      consultation: 'Консультация',
      webinar: 'Вебинар',
      conference: 'Конференция',
      other: 'Другое',
    };
    return labels[category] || category;
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    
    // YouTube
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    const videoId = (match && match[7].length === 11) ? match[7] : null;
    
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}`;
    }
    
    // Vimeo
    const vimeoRegExp = /vimeo\.com\/(\d+)/;
    const vimeoMatch = url.match(vimeoRegExp);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }
    
    return null;
  };

  // Яндекс.Диск и подобные — нельзя встраивать в iframe (CSP), показываем ссылку
  const isYandexDisk = (url) => {
    if (!url) return false;
    return /disk\.yandex\.(ru|com|kz|by|uz|am)|disk\.360\.yandex|yadi\.sk/i.test(url);
  };

  // Плеер для видео с Яндекс.Диска: пробуем прокси, при ошибке — кнопка «Открыть»
  const YandexVideoPlayer = ({ streamUrl, fallbackUrl }) => {
    const [useFallback, setUseFallback] = useState(false);
    return (
      <Box sx={{ position: 'relative', pt: '56.25%', backgroundColor: '#000' }}>
        {!useFallback ? (
          <video
            controls
            playsInline
            preload="metadata"
            onError={() => setUseFallback(true)}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
            }}
          >
            <source src={streamUrl} type="video/mp4" />
            Ваш браузер не поддерживает воспроизведение видео.
          </video>
        ) : (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              bgcolor: 'grey.900',
            }}
          >
            <Typography variant="body1" color="white" sx={{ px: 2, textAlign: 'center' }}>
              Видео на Яндекс.Диске
            </Typography>
            <Button
              variant="contained"
              href={fallbackUrl}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<OpenInNewIcon />}
            >
              Открыть видео
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  const handlePrevImage = (eventId, galleryLength) => {
    setGalleryIndexes(prev => ({
      ...prev,
      [eventId]: (prev[eventId] - 1 + galleryLength) % galleryLength
    }));
  };

  const handleNextImage = (eventId, galleryLength) => {
    setGalleryIndexes(prev => ({
      ...prev,
      [eventId]: (prev[eventId] + 1) % galleryLength
    }));
  };

  const renderMedia = (event) => {
    if (!event) return null;
    
    try {
      const { mediaType, image, videoUrl, gallery } = event || {};

      if (mediaType === 'video' && videoUrl) {
      // 1) Яндекс.Диск — через наш прокси получаем прямую ссылку и воспроизводим в <video>
      if (isYandexDisk(videoUrl)) {
        const streamUrl = getBackendOrigin() + '/api/yandex-stream?url=' + encodeURIComponent(videoUrl);
        return (
          <YandexVideoPlayer streamUrl={streamUrl} fallbackUrl={videoUrl} />
        );
      }

      // 2) Локальный файл или прямая ссылка на видео
      const isLocalOrDirect =
        videoUrl.startsWith('/uploads') ||
        videoUrl.startsWith('uploads') ||
        /\.(mp4|webm|ogg|m4v|mov)$/i.test(videoUrl);

      if (isLocalOrDirect) {
        const src = getFullVideoSrc(videoUrl);
        const mime = getVideoMimeType(videoUrl);
        return (
          <Box sx={{ position: 'relative', pt: '56.25%', backgroundColor: '#000' }}>
            <video
              controls
              playsInline
              preload="metadata"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
              }}
            >
              <source src={src} type={mime} />
              Ваш браузер не поддерживает воспроизведение видео.
            </video>
          </Box>
        );
      }

      // 3) YouTube, Vimeo — iframe
      const embedUrl = getYouTubeEmbedUrl(videoUrl);
      if (embedUrl) {
        return (
          <Box sx={{ position: 'relative', pt: '56.25%' }}>
            <iframe
              src={embedUrl}
              title=""
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
              }}
            />
          </Box>
        );
      }

      // 4) Неизвестный формат — ссылка
      return (
        <Box
          sx={{
            position: 'relative',
            pt: '56.25%',
            backgroundColor: 'grey.200',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Button
            variant="contained"
            href={videoUrl}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<OpenInNewIcon />}
          >
            Открыть видео
          </Button>
        </Box>
      );
      }

      if (mediaType === 'gallery' && gallery && gallery.length > 0) {
      const currentIndex = galleryIndexes[event._id] || 0;
      const imageUrl = getFullImageSrc(gallery[currentIndex]);
      return (
        <Box sx={{ position: 'relative', pt: '56.25%' }}>
          <CardMedia
            component="div"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundImage: `url("${imageUrl}")`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundColor: '#f5f5f5',
            }}
            onError={(e) => {
              console.error('Ошибка загрузки изображения:', imageUrl);
              e.target.style.backgroundColor = '#e0e0e0';
            }}
          />
          {gallery.length > 1 && (
            <>
              <IconButton
                onClick={() => handlePrevImage(event._id, gallery.length)}
                sx={{
                  position: 'absolute',
                  left: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  bgcolor: 'rgba(255, 255, 255, 0.8)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.95)',
                  },
                }}
                size="small"
              >
                <ArrowBackIosNewIcon fontSize="small" />
              </IconButton>
              <IconButton
                onClick={() => handleNextImage(event._id, gallery.length)}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  bgcolor: 'rgba(255, 255, 255, 0.8)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.95)',
                  },
                }}
                size="small"
              >
                <ArrowForwardIosIcon fontSize="small" />
              </IconButton>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  gap: 0.5,
                }}
              >
                {gallery.map((_, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: idx === currentIndex ? 'white' : 'rgba(255, 255, 255, 0.5)',
                      transition: 'all 0.3s',
                    }}
                  />
                ))}
              </Box>
            </>
          )}
        </Box>
      );
      }

      // Одиночное изображение (по умолчанию)
      if (image) {
        const imageUrl = getFullImageSrc(image);
        return (
          <CardMedia
            component="div"
            sx={{
              pt: '56.25%',
              backgroundImage: `url("${imageUrl}")`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundColor: '#f5f5f5',
            }}
          />
        );
      }

      return null;
    } catch (error) {
      console.error('Ошибка рендеринга медиа:', error, event);
      return (
        <Box sx={{ pt: '56.25%', backgroundColor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Ошибка загрузки медиа
          </Typography>
        </Box>
      );
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box id="events" sx={{ py: 8, backgroundColor: 'background.default' }}>
      <Container maxWidth="xl">
        <Typography variant="h2" align="center" sx={{ mb: 2, fontWeight: 700, fontSize: { xs: '2rem', md: '2.75rem' } }}>
          Новости и события
        </Typography>
        <Typography
          variant="h6"
          align="center"
          color="text.secondary"
          sx={{ mb: 6, maxWidth: 800, mx: 'auto', fontSize: { xs: '1rem', md: '1.25rem' } }}
        >
          Бесплатные консультации, вебинары и семинары для родителей по вопросам воспитания и развития детей
        </Typography>

        <Grid container spacing={4}>
          {events.length === 0 ? (
            <Grid item xs={12}>
              <Typography align="center" color="text.secondary">
                В данный момент нет запланированных мероприятий
              </Typography>
            </Grid>
          ) : (
            events.map((event) => {
              if (!event || !event._id) return null;
              
              try {
                return (
                  <Grid item xs={12} md={6} lg={4} key={event._id}>
                    <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    boxShadow: 2,
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 6,
                    },
                  }}
                >
                  {renderMedia(event)}
                  
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Chip
                        label={getCategoryLabel(event.category)}
                        color="primary"
                        size="small"
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        {event.date && !isNaN(new Date(event.date).getTime()) 
                          ? format(new Date(event.date), 'dd MMMM yyyy', { locale: ru })
                          : 'Дата не указана'}
                      </Typography>
                    </Box>
                    
                    {event.description ? (
                      <Box
                        sx={{
                          '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1 },
                          '& h1, & h2, & h3, & h4, & h5, & h6': {
                            marginTop: '0.5em',
                            marginBottom: '0.5em',
                            fontWeight: 'bold',
                          },
                          '& p': {
                            marginBottom: '0.5em',
                            lineHeight: 1.6,
                          },
                          '& ul, & ol': {
                            marginLeft: '20px',
                            marginBottom: '0.5em',
                          },
                          '& a': {
                            color: 'primary.main',
                            textDecoration: 'underline',
                          },
                          '& blockquote': {
                            borderLeft: '4px solid',
                            borderColor: 'primary.main',
                            paddingLeft: '16px',
                            marginLeft: 0,
                            fontStyle: 'italic',
                            color: 'text.secondary',
                          },
                          '& pre': {
                            backgroundColor: '#f5f5f5',
                            padding: '12px',
                            borderRadius: 1,
                            overflow: 'auto',
                          },
                          color: 'text.secondary',
                          fontSize: '0.875rem',
                        }}
                        dangerouslySetInnerHTML={{ __html: event.description || '' }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Описание отсутствует
                      </Typography>
                    )}
                    
                    {event.time && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <EventIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {event.time}
                        </Typography>
                      </Box>
                    )}
                    
                    {event.location && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationOnIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {event.location}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                    </Card>
                  </Grid>
                );
              } catch (error) {
                console.error('Ошибка рендеринга мероприятия:', error, event);
                return null;
              }
            })
          )}
        </Grid>
      </Container>
    </Box>
  );
};

export default EventsSection;
